import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Footer from '../components/landing/Footer';

const PrivacyPolicy = () => {
  const lastUpdated = 'November 25, 2025';
  const effectiveDate = 'November 25, 2025';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl overflow-hidden">
              <img
                src="/img/logo-ag.png"
                alt="AgentGarden Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <span className="text-xl font-bold text-gray-900">AgentGarden</span>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-gray-600 mb-2">Last Updated: {lastUpdated}</p>
          <p className="text-gray-600 mb-8">Effective Date: {effectiveDate}</p>

          <div className="prose prose-lg max-w-none text-gray-700 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
              <p>
                <strong>Switchr Technologies</strong> ("Company," "we," "us," or "our") is committed to protecting
                your privacy and ensuring the security of your personal data. This Privacy Policy explains how we
                collect, use, disclose, and safeguard your information when you use our AgentGarden platform and
                related services (collectively, the "Services").
              </p>
              <p>
                This Privacy Policy complies with:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>European Union:</strong> General Data Protection Regulation (GDPR) and the EU AI Act</li>
                <li><strong>United Kingdom:</strong> UK GDPR and Data Protection Act 2018</li>
                <li><strong>India:</strong> Digital Personal Data Protection Act 2023 (DPDPA) and Information Technology Act 2000</li>
              </ul>
              <p className="mt-4">
                By using our Services, you acknowledge that you have read and understood this Privacy Policy.
                If you do not agree with our practices, please do not use our Services.
              </p>
            </section>

            {/* Data Controller Information */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Data Controller / Data Fiduciary Information</h2>
              <div className="bg-gray-100 p-6 rounded-lg">
                <p><strong>Company Name:</strong> Switchr Technologies</p>
                <p><strong>Registered Address:</strong> A221-224, Sunder Nagar, Near 200 Ft Bypass, Jaipur (Towards Mansarover), Ajmer Road, Jaipur-302021, Rajasthan, India</p>
                <p><strong>Data Protection Contact:</strong> <a href="mailto:hello@agentgarden.co" className="text-primary-600 hover:underline">hello@agentgarden.co</a></p>
                <p><strong>Toll Free:</strong> 1800 123 7008</p>
              </div>
              <p className="mt-4">
                <strong>For EU/UK Users:</strong> We act as the Data Controller for personal data collected directly from you.
                When you use our Services to process your customers' data, you are the Data Controller and we are the Data Processor.
              </p>
              <p className="mt-2">
                <strong>For Indian Users:</strong> We act as the Data Fiduciary under the Digital Personal Data Protection Act 2023.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Information We Collect</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">3.1 Information You Provide Directly</h3>
              <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Category</th>
                    <th className="px-4 py-3 text-left font-semibold">Examples</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 font-medium">Account Information</td>
                    <td className="px-4 py-3">Name, email address, password (hashed), phone number, profile picture</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Billing Information</td>
                    <td className="px-4 py-3">Payment method details, billing address, transaction history (processed via secure third-party payment processors)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Business Information</td>
                    <td className="px-4 py-3">Company name, job title, industry, business address</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Communication Data</td>
                    <td className="px-4 py-3">Support inquiries, feedback, survey responses, correspondence with us</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Workflow Data</td>
                    <td className="px-4 py-3">AI agent configurations, workflow templates, automation rules you create</td>
                  </tr>
                </tbody>
              </table>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.2 Information Collected Automatically</h3>
              <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Category</th>
                    <th className="px-4 py-3 text-left font-semibold">Examples</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 font-medium">Device Information</td>
                    <td className="px-4 py-3">Device type, operating system, browser type and version, unique device identifiers</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Log Data</td>
                    <td className="px-4 py-3">IP address, access times, pages viewed, referring URL, actions taken within Services</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Usage Data</td>
                    <td className="px-4 py-3">Features used, workflows created, AI agent interactions, performance metrics</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Location Data</td>
                    <td className="px-4 py-3">General location derived from IP address (country/region level)</td>
                  </tr>
                </tbody>
              </table>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.3 Information from Third Parties</h3>
              <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Source</th>
                    <th className="px-4 py-3 text-left font-semibold">Data Types</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 font-medium">OAuth Providers (Google, etc.)</td>
                    <td className="px-4 py-3">Name, email, profile picture when you sign in with social accounts</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Integration Partners</td>
                    <td className="px-4 py-3">Data from connected services (Gmail, Slack, Calendar, HubSpot, Zoom) as authorized by you</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Analytics Providers</td>
                    <td className="px-4 py-3">Aggregated usage and performance data</td>
                  </tr>
                </tbody>
              </table>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.4 AI-Related Data Processing</h3>
              <p>Our AI agents may process:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Input Data:</strong> Data you provide to AI agents for processing (emails, documents, queries)</li>
                <li><strong>Output Data:</strong> AI-generated responses, recommendations, and automated actions</li>
                <li><strong>Training Data:</strong> Anonymized and aggregated data to improve AI model performance (with your consent where required)</li>
                <li><strong>Audit Logs:</strong> Records of AI agent actions and decisions for transparency and compliance</li>
              </ul>
            </section>

            {/* Legal Basis for Processing */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Legal Basis for Processing (GDPR/UK GDPR)</h2>
              <p>We process your personal data based on the following legal grounds:</p>
              <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden mt-4">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Legal Basis</th>
                    <th className="px-4 py-3 text-left font-semibold">Processing Activities</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 font-medium">Contract Performance (Art. 6(1)(b))</td>
                    <td className="px-4 py-3">Providing Services, managing your account, processing payments, customer support</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Consent (Art. 6(1)(a))</td>
                    <td className="px-4 py-3">Marketing communications, AI model improvement using your data, optional analytics, cookies (non-essential)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Legitimate Interests (Art. 6(1)(f))</td>
                    <td className="px-4 py-3">Service improvement, fraud prevention, security monitoring, analytics, business development</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Legal Obligation (Art. 6(1)(c))</td>
                    <td className="px-4 py-3">Tax compliance, regulatory reporting, responding to legal requests</td>
                  </tr>
                </tbody>
              </table>
            </section>

            {/* How We Use Information */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. How We Use Your Information</h2>
              <p>We use your personal data for the following purposes:</p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">5.1 Service Delivery</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Create and manage your account</li>
                <li>Provide access to our AI agent platform and features</li>
                <li>Process AI workflow executions and automations</li>
                <li>Enable integrations with third-party services</li>
                <li>Process payments and manage subscriptions</li>
                <li>Provide customer support and respond to inquiries</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">5.2 Service Improvement</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Analyze usage patterns to improve features and user experience</li>
                <li>Develop new products, services, and features</li>
                <li>Train and improve AI models (using anonymized/aggregated data)</li>
                <li>Conduct research and analytics</li>
                <li>Test and debug our Services</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">5.3 Communications</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Send transactional messages (account confirmations, security alerts, service updates)</li>
                <li>Provide product announcements and feature updates</li>
                <li>Send marketing communications (with your consent)</li>
                <li>Respond to your requests and feedback</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">5.4 Security and Compliance</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Detect, prevent, and address fraud, abuse, and security issues</li>
                <li>Verify identity and prevent unauthorized access</li>
                <li>Comply with legal obligations and regulatory requirements</li>
                <li>Enforce our Terms and Conditions and policies</li>
              </ul>
            </section>

            {/* Data Sharing */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. How We Share Your Information</h2>
              <p>We do not sell your personal data. We may share your information with:</p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">6.1 Service Providers</h3>
              <p>Third-party vendors who assist us in providing Services:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Cloud Infrastructure:</strong> Amazon Web Services (AWS) for hosting and storage</li>
                <li><strong>Payment Processing:</strong> Secure payment processors for transactions</li>
                <li><strong>AI/ML Services:</strong> Anthropic, OpenAI for language model capabilities</li>
                <li><strong>Analytics:</strong> Usage analytics and performance monitoring</li>
                <li><strong>Communication:</strong> Email delivery and customer support platforms</li>
              </ul>
              <p className="mt-4">
                All service providers are bound by data processing agreements and are required to protect your data
                in accordance with applicable laws.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">6.2 Integration Partners</h3>
              <p>
                When you connect third-party services (Gmail, Slack, Google Calendar, Zoom, HubSpot), data flows
                between our Services and those platforms as authorized by you. Each integration is subject to
                the third party's privacy policy.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">6.3 Legal Requirements</h3>
              <p>We may disclose your information if required to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Comply with applicable laws, regulations, or legal processes</li>
                <li>Respond to lawful requests from public authorities</li>
                <li>Protect our rights, privacy, safety, or property</li>
                <li>Enforce our Terms and Conditions</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">6.4 Business Transfers</h3>
              <p>
                In the event of a merger, acquisition, or sale of assets, your information may be transferred
                as part of the transaction. We will notify you of any such change and any choices you may have.
              </p>
            </section>

            {/* International Data Transfers */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. International Data Transfers</h2>
              <p>
                Your data may be transferred to and processed in countries other than your country of residence,
                including India and the United States where our servers and service providers are located.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">7.1 EU/UK Data Transfers</h3>
              <p>For transfers of personal data outside the EU/UK, we implement appropriate safeguards:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Adequacy Decisions:</strong> Transfers to countries with adequate protection levels</li>
                <li><strong>Standard Contractual Clauses (SCCs):</strong> EU Commission-approved contracts for data transfers</li>
                <li><strong>Supplementary Measures:</strong> Additional technical and organizational measures where required</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">7.2 India Data Transfers</h3>
              <p>
                Under the Digital Personal Data Protection Act 2023, we ensure that cross-border data transfers
                comply with government notifications regarding permitted jurisdictions. Data transfers are conducted
                with appropriate contractual and security safeguards.
              </p>
            </section>

            {/* Data Retention */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Data Retention</h2>
              <p>We retain your personal data only for as long as necessary for the purposes outlined in this Privacy Policy:</p>
              <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden mt-4">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Data Category</th>
                    <th className="px-4 py-3 text-left font-semibold">Retention Period</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 font-medium">Account Data</td>
                    <td className="px-4 py-3">Duration of your account plus 3 years after deletion</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Workflow/AI Data</td>
                    <td className="px-4 py-3">Duration of account plus 90 days after deletion for backup purposes</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Billing Records</td>
                    <td className="px-4 py-3">7 years for tax and legal compliance</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Support Communications</td>
                    <td className="px-4 py-3">3 years after resolution</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Usage Logs</td>
                    <td className="px-4 py-3">12 months for security and analytics</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Marketing Preferences</td>
                    <td className="px-4 py-3">Until you withdraw consent</td>
                  </tr>
                </tbody>
              </table>
              <p className="mt-4">
                After the retention period, data is securely deleted or anonymized. We may retain anonymized data
                indefinitely for statistical and analytical purposes.
              </p>
            </section>

            {/* Your Rights */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Your Data Protection Rights</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">9.1 Rights Under GDPR/UK GDPR (EU/UK Users)</h3>
              <p>You have the following rights regarding your personal data:</p>
              <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden mt-4">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Right</th>
                    <th className="px-4 py-3 text-left font-semibold">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 font-medium">Access</td>
                    <td className="px-4 py-3">Request a copy of your personal data and information about how we process it</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Rectification</td>
                    <td className="px-4 py-3">Request correction of inaccurate or incomplete personal data</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Erasure ("Right to be Forgotten")</td>
                    <td className="px-4 py-3">Request deletion of your personal data in certain circumstances</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Restriction</td>
                    <td className="px-4 py-3">Request limitation of processing in certain circumstances</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Data Portability</td>
                    <td className="px-4 py-3">Receive your data in a structured, machine-readable format</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Objection</td>
                    <td className="px-4 py-3">Object to processing based on legitimate interests or for direct marketing</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Automated Decision-Making</td>
                    <td className="px-4 py-3">Not be subject to decisions based solely on automated processing with legal effects</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Withdraw Consent</td>
                    <td className="px-4 py-3">Withdraw consent at any time (where processing is based on consent)</td>
                  </tr>
                </tbody>
              </table>
              <p className="mt-4">
                <strong>Lodging Complaints:</strong> You have the right to lodge a complaint with your local data protection
                authority (e.g., ICO in the UK, your national DPA in the EU).
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">9.2 Rights Under DPDPA 2023 (Indian Users)</h3>
              <p>Under India's Digital Personal Data Protection Act 2023, you have the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Access:</strong> Obtain a summary of your personal data and processing activities</li>
                <li><strong>Correction and Erasure:</strong> Request correction of inaccurate data and erasure of data no longer necessary</li>
                <li><strong>Grievance Redressal:</strong> Have your complaints addressed within a reasonable timeframe</li>
                <li><strong>Nomination:</strong> Nominate another person to exercise your rights in case of death or incapacity</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent at any time with prospective effect</li>
              </ul>
              <p className="mt-4">
                <strong>Your Duties:</strong> Under DPDPA 2023, you are expected to provide accurate information, not impersonate
                others, not suppress material information, and comply with applicable laws when exercising rights.
              </p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">9.3 Exercising Your Rights</h3>
              <p>To exercise any of your rights, please contact us at:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Email:</strong> <a href="mailto:hello@agentgarden.co" className="text-primary-600 hover:underline">hello@agentgarden.co</a></li>
                <li><strong>Toll Free:</strong> 1800 123 7008</li>
              </ul>
              <p className="mt-4">
                We will respond to your request within 30 days (or as required by applicable law). We may request
                verification of your identity before processing certain requests.
              </p>
            </section>

            {/* AI-Specific Privacy Provisions */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. AI-Specific Privacy Provisions</h2>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">10.1 Transparency in AI Processing</h3>
              <p>In accordance with the EU AI Act and ethical AI principles:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>You will be informed when interacting with AI-generated content or automated systems</li>
                <li>We provide explanations of how AI agents process your data and make decisions</li>
                <li>Audit logs of AI agent actions are maintained for accountability</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">10.2 AI Model Training</h3>
              <p>Regarding the use of your data for AI improvement:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>We do not use your personal data to train AI models without appropriate legal basis</li>
                <li>Where training occurs, we use anonymized and aggregated data</li>
                <li>You can opt out of having your data used for model improvement in account settings</li>
                <li>Training data does not include sensitive personal information</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">10.3 Automated Decision-Making</h3>
              <p>
                When AI agents make decisions that may significantly affect you, we ensure:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Human review is available for important decisions</li>
                <li>You can request explanation of automated decisions</li>
                <li>You can contest decisions and request human intervention</li>
                <li>We implement safeguards against bias and discrimination</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">10.4 Third-Party AI Services</h3>
              <p>
                Our Services use AI models from third-party providers (Anthropic, OpenAI). Data sent to these
                services is subject to their privacy practices. We implement data processing agreements to
                ensure adequate protection of your data.
              </p>
            </section>

            {/* Data Security */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Data Security</h2>
              <p>We implement comprehensive security measures to protect your personal data:</p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">11.1 Technical Measures</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Encryption:</strong> Data encrypted in transit (TLS 1.3) and at rest (AES-256)</li>
                <li><strong>Access Controls:</strong> Role-based access control and authentication mechanisms</li>
                <li><strong>Infrastructure Security:</strong> AWS security best practices, VPCs, security groups</li>
                <li><strong>Monitoring:</strong> Continuous security monitoring and intrusion detection</li>
                <li><strong>Backup:</strong> Regular encrypted backups with disaster recovery procedures</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">11.2 Organizational Measures</h3>
              <ul className="list-disc pl-6 space-y-2">
                <li>Employee training on data protection and security</li>
                <li>Background checks for personnel with data access</li>
                <li>Security policies and procedures documentation</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Incident response procedures</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">11.3 Breach Notification</h3>
              <p>
                In the event of a personal data breach, we will notify affected individuals and relevant authorities
                as required by applicable law (within 72 hours for GDPR, within reasonable time for DPDPA).
              </p>
            </section>

            {/* Cookies and Tracking */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Cookies and Tracking Technologies</h2>
              <p>We use cookies and similar technologies to enhance your experience:</p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">12.1 Types of Cookies</h3>
              <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">Type</th>
                    <th className="px-4 py-3 text-left font-semibold">Purpose</th>
                    <th className="px-4 py-3 text-left font-semibold">Duration</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 font-medium">Essential</td>
                    <td className="px-4 py-3">Authentication, security, basic functionality</td>
                    <td className="px-4 py-3">Session / 30 days</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Functional</td>
                    <td className="px-4 py-3">Preferences, language settings, personalization</td>
                    <td className="px-4 py-3">1 year</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Analytics</td>
                    <td className="px-4 py-3">Usage statistics, performance monitoring</td>
                    <td className="px-4 py-3">2 years</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">Marketing (with consent)</td>
                    <td className="px-4 py-3">Targeted advertising, campaign measurement</td>
                    <td className="px-4 py-3">90 days</td>
                  </tr>
                </tbody>
              </table>

              <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">12.2 Managing Cookies</h3>
              <p>
                You can manage cookie preferences through your browser settings or our cookie consent banner.
                Note that disabling essential cookies may affect Service functionality.
              </p>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Children's Privacy</h2>
              <p>
                Our Services are not directed to individuals under 18 years of age. We do not knowingly collect
                personal data from children. If we learn that we have collected personal data from a child without
                parental consent, we will take steps to delete such information.
              </p>
              <p className="mt-4">
                <strong>For Indian Users:</strong> In compliance with DPDPA 2023, processing of children's data
                (under 18 years) requires verifiable parental consent, and we do not undertake tracking or behavioral
                monitoring of children.
              </p>
              <p className="mt-4">
                If you believe we have inadvertently collected data from a child, please contact us immediately
                at <a href="mailto:hello@agentgarden.co" className="text-primary-600 hover:underline">hello@agentgarden.co</a>.
              </p>
            </section>

            {/* Third-Party Links */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Third-Party Links and Services</h2>
              <p>
                Our Services may contain links to third-party websites and integrations. This Privacy Policy does not
                apply to such third-party services, and we are not responsible for their privacy practices.
                We encourage you to review the privacy policies of any third-party services you access.
              </p>
            </section>

            {/* Changes to Privacy Policy */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy periodically to reflect changes in our practices, legal requirements,
                or Services. When we make material changes:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>We will notify you via email and/or prominent notice on our Services</li>
                <li>We will update the "Last Updated" date at the top of this policy</li>
                <li>For significant changes, we may seek your renewed consent where required</li>
              </ul>
              <p className="mt-4">
                We encourage you to review this Privacy Policy regularly to stay informed about how we protect your data.
              </p>
            </section>

            {/* Grievance Officer (India) */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">16. Grievance Officer (India)</h2>
              <p>
                In compliance with the Information Technology Act 2000 and Digital Personal Data Protection Act 2023,
                we have appointed a Grievance Officer to address your concerns:
              </p>
              <div className="bg-gray-100 p-6 rounded-lg mt-4">
                <p><strong>Grievance Officer</strong></p>
                <p>Switchr Technologies</p>
                <p>A221-224, Sunder Nagar, Near 200 Ft Bypass</p>
                <p>Jaipur (Towards Mansarover), Ajmer Road</p>
                <p>Jaipur-302021, Rajasthan, India</p>
                <p className="mt-4"><strong>Email:</strong> <a href="mailto:hello@agentgarden.co" className="text-primary-600 hover:underline">hello@agentgarden.co</a></p>
                <p><strong>Toll Free:</strong> 1800 123 7008</p>
              </div>
              <p className="mt-4">
                The Grievance Officer will acknowledge your complaint within 24 hours and resolve it within 30 days
                or as prescribed by applicable law.
              </p>
            </section>

            {/* Contact Us */}
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">17. Contact Us</h2>
              <p>
                For questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-100 p-6 rounded-lg mt-4">
                <p><strong>Switchr Technologies</strong></p>
                <p>A221-224, Sunder Nagar, Near 200 Ft Bypass</p>
                <p>Jaipur (Towards Mansarover), Ajmer Road</p>
                <p>Jaipur-302021, Rajasthan, India</p>
                <p className="mt-4"><strong>Toll Free:</strong> 1800 123 7008</p>
                <p><strong>Email:</strong> <a href="mailto:hello@agentgarden.co" className="text-primary-600 hover:underline">hello@agentgarden.co</a></p>
              </div>
            </section>

            {/* Acknowledgment */}
            <section className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Acknowledgment</h2>
              <p>
                By using our Services, you acknowledge that you have read and understood this Privacy Policy.
                We are committed to protecting your privacy and will handle your personal data responsibly
                and in accordance with applicable data protection laws.
              </p>
            </section>
          </div>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
